import { http } from './request'

/**
 * 与后端 {@code AiAssistantAdminController} 一致：
 * {@code context-path} 为 /api 时，完整路径为 /api/service/ai-assistant/**
 */
const API_PREFIX = '/service/ai-assistant'

/** 顶部统计：今日对话次数、自动回复准确率（与 AiAssistantStatsResult 一致） */
export interface AiAssistantStats {
  todayConversations: number
  replyAccuracyPercent: number
}

/** 关键词回复条目（与 AiKeywordReplyVo 一致） */
export interface KeywordReplyRecord {
  id: string
  title: string
  /** 展示用日期 YYYY-MM-DD */
  date: string
  triggerWords: string[]
  content: string
}

export interface KeywordPageParams {
  page: number
  pageSize: number
}

export interface KeywordPageResult {
  list: KeywordReplyRecord[]
  total: number
}

export interface KeywordReplyPayload {
  title: string
  triggerWords: string[]
  content: string
}

function formatDateYmd(raw: unknown): string {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  return s.length >= 10 ? s.slice(0, 10) : s
}

function formatDateTime(raw: unknown): string {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  return s.length >= 19 ? s.slice(0, 19).replace('T', ' ') : s
}

function parseTriggerWords(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((x) => String(x).trim()).filter(Boolean)
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const a = JSON.parse(raw) as unknown
      if (Array.isArray(a)) return a.map((x) => String(x).trim()).filter(Boolean)
    } catch {
      return []
    }
  }
  return []
}

function mapKeywordRecord(raw: unknown): KeywordReplyRecord {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return {
    id: String(o.id ?? ''),
    title: String(o.title ?? ''),
    date: formatDateYmd(o.date ?? o.createTime ?? o.updateTime),
    triggerWords: parseTriggerWords(o.triggerWords),
    content: String(o.content ?? o.replyContent ?? ''),
  }
}

function normalizePageRecords<T>(
  raw: unknown,
  mapRow: (row: unknown) => T,
): { list: T[]; total: number } {
  if (raw == null || typeof raw !== 'object') return { list: [], total: 0 }
  const o = raw as Record<string, unknown>
  const records = Array.isArray(o.records) ? o.records : Array.isArray(o.list) ? o.list : []
  const totalRaw = o.total
  const total = typeof totalRaw === 'number' ? totalRaw : totalRaw != null ? Number(totalRaw) : 0
  return { list: records.map(mapRow), total: Number.isFinite(total) ? total : 0 }
}

function nStats(v: unknown): number {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function nTemperature(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim()) {
    const x = Number(v)
    return Number.isFinite(x) ? x : 0.7
  }
  return 0.7
}

/** GET /service/ai-assistant/stats */
export async function fetchAiAssistantStats(): Promise<AiAssistantStats> {
  const raw = await http.get<Partial<AiAssistantStats>>(`${API_PREFIX}/stats`)
  return {
    todayConversations: nStats(raw?.todayConversations),
    replyAccuracyPercent: nStats(raw?.replyAccuracyPercent),
  }
}

/** GET /service/ai-assistant/keywords/page（Query：page、pageSize） */
export async function fetchKeywordPage(params: KeywordPageParams): Promise<KeywordPageResult> {
  const raw = await http.get<unknown>(`${API_PREFIX}/keywords/page`, {
    params: { page: params.page, pageSize: params.pageSize },
  })
  const { list, total } = normalizePageRecords(raw, mapKeywordRecord)
  return { list, total }
}

/** POST /service/ai-assistant/keywords */
export async function createKeywordReply(payload: KeywordReplyPayload): Promise<void> {
  await http.post<void>(`${API_PREFIX}/keywords`, payload)
}

/** POST /service/ai-assistant/keywords/edit（body 含 id，与后端 AiKeywordEditParam 一致） */
export async function updateKeywordReply(id: string, payload: KeywordReplyPayload): Promise<void> {
  await http.post<void>(`${API_PREFIX}/keywords/edit`, {
    id: String(id).trim(),
    title: payload.title,
    triggerWords: payload.triggerWords,
    content: payload.content,
  })
}

// —— 对话记录 ——

export type ConversationReplyMode = 'auto' | 'manual' | 'fallback'
export type ConversationChannel = 'web' | 'miniapp' | 'h5'

export interface ConversationRecord {
  id: string
  visitorId: string
  /** 账号名称，后端由 visitorId 关联用户姓名 */
  conversationAccount: string
  userMessage: string
  assistantReply: string
  matchedRuleTitle: string | null
  replyMode: ConversationReplyMode
  channel: ConversationChannel
  createdAt: string
}

export interface ConversationPageParams extends KeywordPageParams {
  keyword?: string
  channel?: ConversationChannel
  /** 对话账号名称（用户姓名模糊） */
  conversationAccount?: string
}

export interface ConversationPageResult {
  list: ConversationRecord[]
  total: number
}

function mapConversationRecord(raw: unknown): ConversationRecord {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const mode = String(o.replyMode ?? 'auto').toLowerCase()
  const ch = String(o.channel ?? 'web').toLowerCase()
  const replyMode: ConversationReplyMode = mode === 'manual' || mode === 'fallback' ? mode : 'auto'
  const channel: ConversationChannel = ch === 'miniapp' || ch === 'h5' ? ch : 'web'
  const mt = o.matchedRuleTitle
  return {
    id: String(o.id ?? ''),
    visitorId: String(o.visitorId ?? ''),
    conversationAccount: String(o.conversationAccount ?? '').trim(),
    userMessage: String(o.userMessage ?? ''),
    assistantReply: String(o.assistantReply ?? ''),
    matchedRuleTitle: mt != null && String(mt).trim() !== '' ? String(mt).trim() : null,
    replyMode,
    channel,
    createdAt: formatDateTime(o.createdAt ?? o.createTime),
  }
}

/** GET /service/ai-assistant/conversations/page（Query：page、pageSize、keyword、channel、conversationAccount） */
export async function fetchConversationPage(
  params: ConversationPageParams,
): Promise<ConversationPageResult> {
  const raw = await http.get<unknown>(`${API_PREFIX}/conversations/page`, {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      keyword: params.keyword?.trim() || undefined,
      channel: params.channel,
      conversationAccount: params.conversationAccount?.trim() || undefined,
    },
  })
  const { list, total } = normalizePageRecords(raw, mapConversationRecord)
  return { list, total }
}

// —— AI 设置 ——

export interface AiSettings {
  modelName: string
  temperature: number
  maxTokens: number
  systemPrompt: string
  enableWebSearch: boolean
  replyLanguage: 'zh' | 'en' | 'auto'
  serviceTimeEnabled: boolean
  serviceTimeStart: string
  serviceTimeEnd: string
  /** 小程序欢迎语（后端 AiSettingsVo，可选） */
  welcomeMessage?: string
}

export type AiSettingsPayload = AiSettings

/** GET /service/ai-assistant/settings */
export async function fetchAiSettings(): Promise<AiSettings> {
  const raw = await http.get<Partial<AiSettings & { temperature?: unknown }>>(
    `${API_PREFIX}/settings`,
  )
  return {
    modelName: String(raw?.modelName ?? 'gpt-4o-mini'),
    temperature: nTemperature(raw?.temperature),
    maxTokens: typeof raw?.maxTokens === 'number' ? raw.maxTokens : 2048,
    systemPrompt: String(raw?.systemPrompt ?? ''),
    enableWebSearch: Boolean(raw?.enableWebSearch),
    replyLanguage: (raw?.replyLanguage === 'en' || raw?.replyLanguage === 'auto'
      ? raw.replyLanguage
      : 'zh') as AiSettings['replyLanguage'],
    serviceTimeEnabled: raw?.serviceTimeEnabled !== false,
    serviceTimeStart: String(raw?.serviceTimeStart ?? '09:00'),
    serviceTimeEnd: String(raw?.serviceTimeEnd ?? '18:00'),
    ...(raw?.welcomeMessage != null && String(raw.welcomeMessage).trim() !== ''
      ? { welcomeMessage: String(raw.welcomeMessage) }
      : {}),
  }
}

/** PUT /service/ai-assistant/settings（body：AiSettingsUpdateParam） */
export async function updateAiSettings(payload: AiSettingsPayload): Promise<void> {
  await http.post<void>(`${API_PREFIX}/settings`, {
    modelName: payload.modelName,
    temperature: payload.temperature,
    maxTokens: payload.maxTokens,
    systemPrompt: payload.systemPrompt,
    enableWebSearch: payload.enableWebSearch,
    replyLanguage: payload.replyLanguage,
    serviceTimeEnabled: payload.serviceTimeEnabled,
    serviceTimeStart: payload.serviceTimeStart,
    serviceTimeEnd: payload.serviceTimeEnd,
    ...(payload.welcomeMessage != null && String(payload.welcomeMessage).trim() !== ''
      ? { welcomeMessage: String(payload.welcomeMessage).trim() }
      : {}),
  })
}
